import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Wind, Snowflake, Layers, Building2, Thermometer, Zap, Volume2, Eye, Leaf, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";

// All climatisation types with detailed advantages — content from kap-clim.fr expertise
const TYPES = [
  {
    slug: "gainable",
    name: "Climatisation gainable",
    badge: "Le plus discret",
    image: "/uploads/cms/services/kap-gainable-bouches.webp",
    images: [
      "/uploads/cms/services/kap-gainable-bouches.webp",
      "/uploads/cms/hero/kap-gainable-conduits.webp",
      "/uploads/cms/services/kap-gainable-bouche-plafond-sejour.webp",
    ],
    icon: Layers,
    pitch: "L'invisibilité totale. Une seule unité, plusieurs pièces climatisées via des gaines dans les faux-plafonds.",
    advantages: [
      { icon: Eye, label: "100% invisible", text: "Aucun bloc apparent — esthétique préservée." },
      { icon: Volume2, label: "Ultra silencieux", text: "Le moteur est éloigné des pièces de vie." },
      { icon: Layers, label: "Multi-zones", text: "Climatisez toute la maison avec un seul appareil." },
      { icon: ShieldCheck, label: "Valeur immobilière", text: "Une installation qui valorise votre bien." },
    ],
    target: "Idéal en construction neuve ou rénovation lourde.",
    featured: true,
  },
  {
    slug: "split-mural",
    name: "Split mural réversible",
    badge: "Le best-seller",
    image: "/uploads/cms/hero/kap-split-facade-provence.webp",
    images: [
      "/uploads/cms/hero/kap-split-facade-provence.webp",
    ],
    icon: Wind,
    pitch: "La solution la plus accessible et la plus rapide à installer. Chauffe en hiver, rafraîchit en été.",
    advantages: [
      { icon: Zap, label: "Pose rapide", text: "Installation en une journée." },
      { icon: Thermometer, label: "Réversible", text: "Chaud + froid avec le même appareil." },
      { icon: Leaf, label: "Économique", text: "COP > 4 — jusqu'à 4 kWh restitués pour 1 kWh consommé." },
      { icon: ShieldCheck, label: "Fiable", text: "Marques DAIKIN, MITSUBISHI, HEIWA, Hitachi." },
    ],
    target: "Parfait pour une pièce ou un appartement.",
    featured: false,
  },
  {
    slug: "multisplit",
    name: "Multisplit",
    badge: "Le polyvalent",
    image: "/uploads/cms/services/kap-console-sol-daikin.webp",
    images: [
      "/uploads/cms/services/kap-console-sol-daikin.webp",
    ],
    icon: Snowflake,
    pitch: "Une seule unité extérieure pour 2 à 5 unités intérieures. Chaque pièce sa température.",
    advantages: [
      { icon: Layers, label: "Jusqu'à 5 zones", text: "Pilotez chaque pièce indépendamment." },
      { icon: Eye, label: "Une seule unité ext.", text: "Esthétique extérieure préservée." },
      { icon: Zap, label: "Économique", text: "Moins cher qu'autant de splits indépendants." },
      { icon: Thermometer, label: "Réversible", text: "Chaud / froid sur chaque zone." },
    ],
    target: "Idéal pour un T3/T4 ou une maison sans gainable.",
    featured: false,
  },
  {
    slug: "pompe-a-chaleur",
    name: "Pompe à chaleur réversible",
    badge: "Le plus écologique",
    image: "/uploads/cms/services/kap-pac-split-mitsubishi-mural.webp",
    images: [
      "/uploads/cms/services/kap-pac-split-mitsubishi-mural.webp",
    ],
    icon: Thermometer,
    pitch: "Chauffer en hiver, rafraîchir en été avec la même machine. Une énergie gratuite puisée dans l'air extérieur.",
    advantages: [
      { icon: Leaf, label: "Énergie renouvelable", text: "Jusqu'à 75 % de l'énergie puisée dans l'air." },
      { icon: Zap, label: "Éligible aux aides", text: "CEE + aides locales selon modèle et usage." },
      { icon: Thermometer, label: "Chaud & froid", text: "Chauffage performant l'hiver, clim l'été." },
      { icon: ShieldCheck, label: "Marques premium", text: "DAIKIN, MITSUBISHI, HEIWA, Hitachi." },
    ],
    target: "Idéale pour remplacer un chauffage électrique vieillissant.",
    featured: false,
  },
  {
    slug: "climatisation-pro",
    name: "Climatisation professionnelle",
    badge: "Pour les entreprises",
    image: "/uploads/cms/hero/kap-skyair-duo.webp",
    images: [
      "/uploads/cms/hero/kap-skyair-duo.webp",
    ],
    icon: Building2,
    pitch: "Bureaux, commerces, restaurants, locaux d'activité — équipements puissants et silencieux.",
    advantages: [
      { icon: Building2, label: "Tous bâtiments", text: "Bureau, commerce, salle d'attente, atelier." },
      { icon: Volume2, label: "Silencieux", text: "Confort sonore essentiel en milieu pro." },
      { icon: Zap, label: "Pose rapide", text: "Minimisation de l'impact sur votre activité." },
      { icon: ShieldCheck, label: "Contrat d'entretien", text: "Maintenance annuelle pour rester en règle." },
    ],
    target: "TPE, PME, commerces, professions libérales.",
    featured: false,
  },
];

export default function ClimatisationSection() {
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="climatisation-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[color:var(--brand-sky)]/10 text-[color:var(--brand-sky)] text-xs font-bold uppercase tracking-[0.2em] mb-4">
            <Wind className="w-3.5 h-3.5" /> Notre expertise n°1
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-[color:var(--brand-navy)] leading-[1] tracking-tight">
            Spécialistes <span className="brand-gradient-text">climatisation</span><br />
            pour particuliers & professionnels
          </h2>
          <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
            Du <strong className="text-[color:var(--brand-navy)]">gainable invisible</strong> au split mural réversible, en passant par la pompe à chaleur écologique, nous installons toutes les solutions de climatisation. Étude personnalisée, marques premium, garantie décennale.
          </p>
        </div>

        {/* Featured: Gainable — large hero card */}
        {TYPES.filter(t => t.featured).map((t, i) => (
          <FeaturedClimCard key={t.slug} type={t} />
        ))}

        {/* Other types — grid */}
        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {TYPES.filter(t => !t.featured).map((t) => <ClimCard key={t.slug} type={t} />)}
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <Link to="/devis">
            <button className="rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white font-bold px-6 h-12 inline-flex items-center gap-2 transition-all shadow-lg hover:shadow-xl" data-testid="clim-section-devis">
              Étude gratuite — devis sous 24h
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </Link>
          <Link to="/services" className="text-sm font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] underline underline-offset-4">
            Voir tous nos services
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturedClimCard({ type }) {
  const Icon = type.icon;
  return (
    <div className="relative rounded-3xl overflow-hidden grid lg:grid-cols-[1.1fr_1fr] shadow-[0_30px_80px_-20px_rgba(30,58,95,0.5)] ring-1 ring-[color:var(--brand-navy)]/10" data-testid={`clim-featured-${type.slug}`}>
      {/* Top accent bar — sky → red gradient signaling "featured" */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[color:var(--brand-sky)] via-white to-[color:var(--brand-red)] z-10" />

      {/* Left — image carousel with strong badge */}
      <div className="relative min-h-[320px] lg:min-h-[520px] bg-[color:var(--brand-navy)]">
        <FeaturedCarousel images={type.images?.length ? type.images : [type.image]} alt={type.name} />
        <div className="absolute inset-0 bg-gradient-to-tr from-[color:var(--brand-navy)]/70 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-white/5 pointer-events-none" />
        <div className="absolute top-6 left-6 flex flex-col gap-2 z-10 pointer-events-none">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[color:var(--brand-red)] text-white text-[11px] font-black uppercase tracking-wider shadow-lg">
            ★ {type.badge}
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[color:var(--brand-navy)] text-[10px] font-black uppercase tracking-wider shadow-lg">
            Solution recommandée n°1
          </div>
        </div>
      </div>

      {/* Right — LIGHT premium content panel for MAXIMUM readability */}
      <div className="relative p-7 md:p-10 lg:p-12 flex flex-col justify-center bg-gradient-to-br from-white via-white to-[color:var(--brand-sky)]/10">
        {/* Decorative corner */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[color:var(--brand-sky)]/10 blur-3xl pointer-events-none" />

        <div className="relative inline-flex items-center gap-2 mb-4">
          <Icon className="w-6 h-6 text-[color:var(--brand-sky)]" />
          <span className="text-xs uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-black">Climatisation · À mettre en avant</span>
        </div>
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black leading-[1.05] text-[color:var(--brand-navy)]">
          {type.name}
        </h3>
        <p className="mt-4 text-[hsl(var(--foreground))]/85 text-base md:text-lg leading-relaxed font-medium">
          {type.pitch}
        </p>

        <div className="mt-7 grid sm:grid-cols-2 gap-3">
          {type.advantages.map((a, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3.5 rounded-xl bg-[color:var(--brand-navy)] text-white shadow-[0_8px_20px_-8px_rgba(30,58,95,0.4)] hover:-translate-y-0.5 transition-transform"
            >
              <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-sky)] flex items-center justify-center flex-shrink-0">
                <a.icon className="w-4.5 h-4.5 text-white" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-display font-black text-sm text-white">{a.label}</div>
                <div className="text-[11px] text-white/80 leading-snug mt-0.5">{a.text}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-[color:var(--brand-navy)]/10 flex flex-wrap items-center gap-4">
          <div className="text-sm text-[hsl(var(--muted-foreground))]">
            <strong className="text-[color:var(--brand-navy)]">Cible :</strong> {type.target}
          </div>
          <Link
            to="/devis"
            className="ml-auto inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[color:var(--brand-red)] hover:bg-[color:var(--brand-red)]/90 text-white font-bold text-sm shadow-lg transition-all hover:-translate-y-0.5"
            data-testid="clim-featured-cta"
          >
            Devis gainable gratuit
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function ClimCard({ type }) {
  const Icon = type.icon;
  const imgs = (type.images?.length ? type.images : (type.image ? [type.image] : [])).filter(Boolean);
  return (
    <article className="group relative rounded-2xl overflow-hidden bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))] flex flex-col md:flex-row shadow-hover" data-testid={`clim-${type.slug}`}>
      <div className="md:w-2/5 relative h-48 md:h-auto overflow-hidden flex-shrink-0 bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c]">
        {imgs.length > 0 ? (
          <FeaturedCarousel images={imgs} alt={type.name} variant="compact" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40">
            <Icon className="w-16 h-16" />
          </div>
        )}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/95 text-[color:var(--brand-navy)] text-[10px] font-bold uppercase tracking-wider z-10 pointer-events-none">
          {type.badge}
        </div>
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <Icon className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <span className="text-[11px] uppercase tracking-wider text-[color:var(--brand-sky)] font-bold">Climatisation</span>
        </div>
        <h3 className="font-display text-xl font-bold text-[color:var(--brand-navy)] leading-tight">{type.name}</h3>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-snug line-clamp-2">{type.pitch}</p>
        <ul className="mt-4 space-y-1.5 flex-1">
          {type.advantages.slice(0, 3).map((a, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <a.icon className="w-3.5 h-3.5 text-[color:var(--brand-sky)] flex-shrink-0 mt-0.5" />
              <span><strong className="text-[color:var(--brand-navy)]">{a.label}.</strong> <span className="text-[hsl(var(--muted-foreground))]">{a.text}</span></span>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-3 border-t border-[hsl(var(--border))] text-[11px] text-[hsl(var(--muted-foreground))]">
          {type.target}
        </div>
      </div>
    </article>
  );
}


function FeaturedCarousel({ images, alt, variant = "featured" }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = images.length;

  useEffect(() => {
    if (n <= 1 || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), variant === "compact" ? 4500 : 5000);
    return () => clearInterval(t);
  }, [n, paused, variant]);

  const go = (delta) => setIdx((i) => (i + delta + n) % n);
  const compact = variant === "compact";
  const arrowSize = compact ? "w-8 h-8" : "w-10 h-10";
  const iconSize = compact ? "w-4 h-4" : "w-5 h-5";
  const arrowOffset = compact ? "left-2 right-2" : "left-3 right-3";
  const dotsBottom = compact ? "bottom-2.5" : "bottom-5";
  const imgCls = compact ? "group-hover:scale-105 transition-transform duration-700" : "";

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`clim-carousel-${variant}`}
    >
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`${alt} — photo ${i + 1}`}
          loading={i === 0 ? "eager" : "lazy"}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${i === idx ? "opacity-100" : "opacity-0"} ${imgCls}`}
        />
      ))}

      {n > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(-1); }}
            className={`absolute ${arrowOffset.split(" ")[0]} top-1/2 -translate-y-1/2 z-30 ${arrowSize} rounded-full bg-white/90 hover:bg-white text-[color:var(--brand-navy)] flex items-center justify-center shadow-xl backdrop-blur-sm transition-all hover:scale-110`}
            aria-label="Photo précédente"
            data-testid={`clim-carousel-prev-${variant}`}
          >
            <ChevronLeft className={iconSize} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(1); }}
            className={`absolute ${arrowOffset.split(" ")[1]} top-1/2 -translate-y-1/2 z-30 ${arrowSize} rounded-full bg-white/90 hover:bg-white text-[color:var(--brand-navy)] flex items-center justify-center shadow-xl backdrop-blur-sm transition-all hover:scale-110`}
            aria-label="Photo suivante"
            data-testid={`clim-carousel-next-${variant}`}
          >
            <ChevronRight className={iconSize} />
          </button>

          <div className={`absolute ${dotsBottom} left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 px-3 py-2 rounded-full bg-[color:var(--brand-navy)]/90 ring-1 ring-white/20 shadow-xl backdrop-blur-md`}>
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                aria-label={`Aller à la photo ${i + 1}`}
                className={`rounded-full transition-all ${i === idx ? (compact ? "w-5 h-1.5 bg-white" : "w-7 h-2 bg-white") : (compact ? "w-1.5 h-1.5 bg-white/60 hover:bg-white/90" : "w-2 h-2 bg-white/60 hover:bg-white/90")}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
